import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

/**
 * Shared race-safe insights + summary fetch lifecycle for Optimizer V2 pages.
 *
 * Page owns URL/body building; this hook owns AbortController, request ids,
 * loading, and applying the latest successful result.
 *
 * @param {object} options
 * @param {boolean} [options.enabled=true]
 * @param {string} [options.errorMessage="Failed to fetch data"]
 * @param {(error: any) => void} [options.onError]
 */
export default function useWrapperInsightsQuery({
  enabled = true,
  errorMessage = "Failed to fetch data",
  onError,
} = {}) {
  const [rows, setRows] = useState([]);
  const [summaryData, setSummaryData] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const latestRequestIdRef = useRef(0);
  const activeControllerRef = useRef(null);

  const abortActive = useCallback(() => {
    if (activeControllerRef.current) {
      activeControllerRef.current.abort();
      activeControllerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      abortActive();
    };
  }, [abortActive]);

  const resetResult = useCallback(() => {
    setRows([]);
    setSummaryData({});
    setTotalCount(0);
    setIsLoading(false);
  }, []);

  /**
   * @param {() => Promise<{ rows?: any[], summaryData?: object, totalCount?: number } | null | undefined> | Promise<void>} fetcher
   *   Called with `{ signal, requestId }`. Return payload to apply, or null to skip apply.
   */
  const runQuery = useCallback(
    async (fetcher) => {
      if (!enabled) return null;

      abortActive();
      const requestId = latestRequestIdRef.current + 1;
      latestRequestIdRef.current = requestId;
      const controller = new AbortController();
      activeControllerRef.current = controller;
      setIsLoading(true);

      try {
        const result = await fetcher({
          signal: controller.signal,
          requestId,
        });

        if (requestId !== latestRequestIdRef.current) return null;

        if (result && typeof result === "object") {
          if ("rows" in result) setRows(result.rows ?? []);
          if ("summaryData" in result) setSummaryData(result.summaryData ?? {});
          if ("totalCount" in result) {
            setTotalCount(Number(result.totalCount) || 0);
          }
        }

        return result ?? null;
      } catch (error) {
        if (
          axios.isCancel?.(error) ||
          error?.name === "CanceledError" ||
          error?.code === "ERR_CANCELED"
        ) {
          return null;
        }
        if (requestId !== latestRequestIdRef.current) return null;

        console.error(error);
        if (onError) {
          onError(error);
        } else {
          toast.error(
            error?.response?.data?.message ?? error?.message ?? errorMessage
          );
        }
        resetResult();
        return null;
      } finally {
        if (requestId === latestRequestIdRef.current) {
          setIsLoading(false);
          if (activeControllerRef.current === controller) {
            activeControllerRef.current = null;
          }
        }
      }
    },
    [abortActive, enabled, errorMessage, onError, resetResult]
  );

  return {
    rows,
    setRows,
    summaryData,
    setSummaryData,
    totalCount,
    setTotalCount,
    isLoading,
    setIsLoading,
    runQuery,
    resetResult,
    abortActive,
    latestRequestIdRef,
  };
}
